import axios from 'axios';

// eslint-disable-next-line import/prefer-default-export
export class Connector {
  static LOADING_TYPE = 1;

  static DELIVERY_TYPE = 2;

  static instance = null;

  constructor() {
    this.init();
  }

  static getInstance() {
    if (Connector.instance === null) Connector.instance = new Connector();
    return Connector.instance;
  }

  static generateError(error) {
    if (error.response) {
      return error.response.data;
    }
    if (error.request) {
      return error.request;
    }
    return error.message;
  }

  init() {
    this.http = axios.create({
      baseURL: process.env.VUE_APP_API,
    });
    this.httpNextories = axios.create({
      baseURL: process.env.VUE_APP_API_NEXTORIES,
    });
    this.http.defaults.headers.post['Content-Type'] = 'application/json';
  }

  getProfile() {
    return this._getData('/account/profile/');
  }

  createPassword({ new_password }) {
    return this._postData('/account/password/', { new_password });
  }

  autoLogin(auth_key, project_token) {
    return new Promise((resolve, reject) => {
      this.http.post('account/auto-login/',
        { auth_key, project_token })
        .then((response) => {
          const bearer = response.data.token;
          this.setBearer(bearer);
          resolve(bearer);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  setBearer(bearer) {
    this.http.defaults.headers.common.Authorization = `Bearer ${bearer}`;
  }

  setProjectToken(token) {
    this.projectToken = token;
  }

  /**
   * Log the user in the backend and save the bearer token in a cookie;
   * @param username {String} address email of the user
   * @param password {String} password of the user
   * @returns {Promise<void>} resolve with nothing if the user can connect with the backend
   * reject with the error message otherwise
   */
  login(username, password) {
    return new Promise((resolve, reject) => {
      this.http.post('account/login/', { username, password })
        .then((response) => {
          const bearer = response.data.token;
          this.setBearer(bearer);
          resolve(bearer);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Retrieve the project token for the current user and save it into a cookie
   * @returns {Promise<boolean>} resolve true if the token has been retrieve false otherwise
   */
  getActiveProjectToken() {
    return new Promise((resolve) => {
      this.http.get('booking/token/')
        .then((response) => {
          this.projectToken = response.data.token;
          resolve(true);
        })
        .catch(() => {
          resolve(false);
        });
    });
  }

  /**
   * Retrieve the project of the current user from the backend
   * @returns {Promise<Object>} resolve with the project for the current user
   * or reject with the backend error message
   */
  getProject() {
    return new Promise((resolve, reject) => {
      this.http.post(`/booking/project/${this.projectToken}`)
        .then((response) => {
          const project = {
            booking_step: response.data.booking_step,
            token: response.data.token,
            ...response.data.data,
          };
          resolve(project);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getProjectRoomsAndCartons() {
    return new Promise((resolve, reject) => {
      this.getProject()
        .then(project => this.getRooms().then(rooms => ({ project, rooms })))
        .then(projectAndRooms => this.getBoxes().then((cartons) => {
          resolve({
            project: projectAndRooms.project,
            rooms: projectAndRooms.rooms,
            cartons,
          });
        }))
        .catch((error) => {
          reject(Connector.generateError(error));
        });
    });
  }

  cancelProject(status) {
    return this._postData(`/booking/cancel/${this.projectToken}`, status);
  }

  /**
   * Validate current project.
   * Mandatory call at the end online booking
   * @returns {Promise<boolean>} resolve true or reject with the error
   */
  validateProject() {
    return new Promise((resolve, reject) => {
      this.http.post(`/booking/project/validate/${this.projectToken}`)
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Post the loading address to the backend
   * @param address
   */
  postLoadingAddress(address) {
    if (address.address && address.address.country === null) address.address.country = 'FR';
    return this._postAddress(address, Connector.LOADING_TYPE);
  }

  /**
   * Post the delivery address to the backend
   * @param address
   */
  postDeliveryAddress(address) {
    if (address.address && address.address.country === null) address.address.country = 'FR';
    return this._postAddress(address, Connector.DELIVERY_TYPE);
  }

  deleteLoadingParking() {
    return this._deleteData(`/booking/parking/${this.projectToken}/${Connector.LOADING_TYPE}`);
  }

  deleteDeliveryParking() {
    return this._deleteData(`/booking/parking/${this.projectToken}/${Connector.DELIVERY_TYPE}`);
  }

  /**
   * Post an address to the backend
   * @param address {object} defined in the api guide
   * @param type {1 || 2} 1 send loading address, 2 send delivery address
   * @returns {Promise<boolean>} resolve true or reject with backend error object.
   * @private
   */
  _postAddress(address, type) {
    if (address.street === '') address.street = `${address.city}, France`;
    return this._postData(`/booking/address/${this.projectToken}/${type}`, address);
  }

  /**
   * Retrieve loading address
   * @returns {Promise<Address>}
   */
  getLoadingAddress() {
    return this._getAddress(Connector.LOADING_TYPE);
  }

  /**
   * Retrieve delivery address
   * @returns {Promise<Address>}
   */
  getDeliveryAddress() {
    return this._getAddress(Connector.DELIVERY_TYPE);
  }

  /**
   * Retrieve address from backend
   * @param type {1 || 2} 1 send loading address, 2 send delivery address
   * @returns {Promise<Address>} Object containing address and floor
   * @private
   */
  _getAddress(type) {
    return this._getData(`/booking/address/${this.projectToken}/${type}`);
  }

  /**
   * Post loading parking description to backend
   * @param parking
   * @returns {Promise<boolean>}
   */
  postLoadingParking(parking) {
    return this._postParking(parking, Connector.LOADING_TYPE);
  }

  /**
   * Post delivery parking description to backend
   * @param parking
   * @returns {Promise<boolean>}
   */
  postDeliveryParking(parking) {
    return this._postParking(parking, Connector.DELIVERY_TYPE);
  }

  /**
   * Post a parking description to the backend
   * @param parking {Object}
   * @param type {1 || 2} 1 send loading address, 2 send delivery address
   * @returns {Promise<boolean>} return true if the response status === 200
   * reject with error otherwise
   * @private
   */
  _postParking(parking, type) {
    return this._postData(`/booking/parking/${this.projectToken}/${type}`, parking);
  }

  /**
   * Retrieve loading parking information
   * @returns {Promise<Parking>}
   */
  getLoadingParking() {
    return this._getParking(Connector.LOADING_TYPE);
  }

  /**
   * Retrieve delivery parking information
   * @returns {Promise<Parking>}
   */
  getDeliveryParking() {
    return this._getParking(Connector.DELIVERY_TYPE);
  }

  /**
   * Retrieve parking information from backend
   * @param type {1 || 2} 1 send loading address, 2 send delivery address
   * @returns {Promise<Parking>} Object containing address and floor
   * @private
   */
  _getParking(type) {
    return this._getData(`/booking/parking/${this.projectToken}/${type}`);
  }

  postDates(dates) {
    return this._postData(`/booking/dates/${this.projectToken}`, dates);
  }

  getDates() {
    return this._getData(`/booking/dates/${this.projectToken}`);
  }

  postInsurance(insurance) {
    return this._postData(`/booking/insurance/${this.projectToken}`, insurance);
  }

  getInsurance() {
    return this._getData(`/booking/insurance/${this.projectToken}`);
  }

  postServices(services) {
    return this._postData(`/booking/services/${this.projectToken}`, services);
  }

  getServices() {
    return this._getData(`/booking/services/${this.projectToken}`);
  }

  getSpecialService() {
    return this._getData(`/customer/special-services/${this.projectToken}`);
  }

  createRoom(room) {
    return this._postData(`/booking/inventory/room/${this.projectToken}`, room);
  }

  saveRooms(rooms, projectId) {
    const newRooms = rooms.filter(r => r.id === undefined)
      .map(room => ({
        name: `${room.name}`,
        project: projectId,
        piece_type: room.piece_type,
      }));

    return new Promise((resolve, reject) => {
      const promises = [];
      for (let i = 0; i < newRooms.length; i += 1) {
        promises.push(
          this._postData(
            `booking/inventory/room/${this.projectToken}`,
            newRooms[i],
          ),
        );
      }
      Promise.all(promises)
        .then((result) => {
          resolve(result);
        })
        .catch(reject);
    });
  }

  getRooms() {
    return this._getData(`/booking/inventory/rooms/${this.projectToken}`);
  }

  getRoomType() {
    return this._getData('static/inventory/room/types/', this.httpNextories);
  }

  getRoom(id) {
    return this._getData(`/booking/inventory/room/${this.projectToken}/${id}`);
  }

  updateRoom(id, data) {
    return this._updateData(`/booking/inventory/room/${this.projectToken}/${id}`, data);
  }

  updateRooms(rooms) {
    return this._postData(`/booking/inventory/rooms/${this.projectToken}`, rooms);
  }

  deleteRoom(id) {
    return this._deleteData(`/booking/inventory/room/${this.projectToken}/${id}`);
  }

  postBoxes(boxes) {
    return this._postData(`/booking/inventory/cartons/${this.projectToken}`, boxes);
  }

  getBoxes() {
    return this._getData(`/booking/inventory/cartons/${this.projectToken}`);
  }

  getFurnitureTypes() {
    return this._getData('/static/inventory/room/furniture-types/', this.httpNextories);
  }

  getEstimationResults() {
    return this._getData(`/booking/estimation/${this.projectToken}`);
  }

  getFormulaAndService() {
    return this._getData(`/booking/formula-service/${this.projectToken}`);
  }

  setStep(booking_step) {
    return this._postData(`booking/booking-step/${this.projectToken}`, { booking_step });
  }

  postPicture(picture) {
    return this.http.post(`booking/upload/image/${this.projectToken}`,
      { picture },
      {
        headers: {
          'Content-Disposition': 'attachment; filename=file',
        },
      });
  }

  _postData(url, data) {
    return new Promise((resolve, reject) => {
      this.http.post(url, data)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(Connector.generateError(error));
        });
    });
  }

  _updateData(url, data) {
    return new Promise((resolve, reject) => {
      this.http.put(url, data)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(Connector.generateError(error));
        });
    });
  }

  _getData(url, api) {
    const baseApi = api || this.http;
    return new Promise((resolve, reject) => {
      baseApi.get(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(Connector.generateError(error));
        });
    });
  }

  _deleteData(url) {
    return new Promise((resolve, reject) => {
      this.http.delete(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(Connector.generateError(error));
        });
    });
  }
}


window.connector = new Connector();
